import { User } from './User';
import {Nullable} from "../common/Nullable";
import {InvoiceOffice, Office} from "../common/Office";
import {Communication} from "../common/Communication";
import { Workweek} from "../calendar/Workday";
import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {Identifier} from "../common/Identifier";
import {UserIdProp} from "./AuthenticatedUser";
import {WorkingHours} from "../timesheet/WorkingHours";
import {Money} from "../accounting/Finance";
import { Qualification } from '../qualifications/Qualification';
import {SoftDeletable} from "../common/SoftDeletable";
import {Person} from "../common/Person";
import {Corporation} from "../common/Corporation";
import {TaxIdentifiable} from "../accounting/TaxIdentifiable";
import {types} from "sass";
import Null = types.Null;
import {Sensitive} from "../common/Sensitive";
import {EmployerBankingInfo} from "../employer/EmployerBankingInfo";

export const EMPLOYEE_AREA_NAME = "employees";

export const EmployeeModule: Module = {
    identifier: "employees",
    isPrivileged: (authUser, privilege) => isPrivileged( authUser, EMPLOYEE_AREA_NAME, EmployeeModule.identifier, privilege )
};
export const EmployeeContractModule: Module = {
    identifier: "employee_contracts",
    isPrivileged: (authUser, privilege) => isPrivileged( authUser, EMPLOYEE_AREA_NAME, EmployeeContractModule.identifier, privilege )
};
export const EmployeeQualificationsModule: Module = {
    identifier: "employee_qualifications",
    isPrivileged: ( authUser, privilege ) => isPrivileged( authUser, EMPLOYEE_AREA_NAME, EmployeeQualificationsModule.identifier, privilege )
};

/**
 * Represents a comprehensive view of an employee within the organization.
 *
 * This interface is used to store and handle detailed information about an employee,
 * including personal details, professional qualifications, and current associations with contracts or offices.
 */
export interface Employee extends EmployeeShort {
    birthday: Nullable< Date >
    currentContract: Nullable< EmployeeContract >
    currentOffice: Nullable< Office >
    qualifications: EmployeeQualification[]
    contactInfo: Communication[]
    isDeleted: boolean
    fullName: string
}

/**
 * Represents a summarized version of an employee's profile for scenarios where only limited information is needed.
 * Intended for use in components, APIs, or systems that require a lighter data model.
 *
 * This interface builds on the `User` interface by adding a shorthand name, which condenses the employee's full name
 * for more concise display or reference in user interfaces or reports.
 */
export interface EmployeeShort extends User {
    shorthandName: string
}

/**
 * Represents a lightweight reference to an employee's summary information.
 *
 * This interface is designed to encapsulate a concise view of an employee's data,
 * commonly used to provide quick-loading or minimalistic representations within
 * user interfaces or data transfer objects.
 */
export interface EmployeeShortProp {
    employee: EmployeeShort
}

/**
 * Represents a request payload to create or update an employee in the system.
 * This is used to capture all employee-related details for processing.
 *
 * This model builds upon the `Person` interface to include employee-specific
 * information such as office assignment, qualifications, and access groups.
 * The structure of this object ensures all relevant employee details are captured
 * for proper handling by internal systems.
 *
 * - For non-technical users: This defines the inputs required to manage employee information.
 * - For developers: This interface extends the `Person` base and provides optional
 *   and nullable fields that need to be accounted for during implementation.
 *
 * Constraints and Edge Cases:
 * - Some fields may explicitly allow `null` (e.g., `id`, `password`, `contract`),
 *   indicating optional or incomplete employee details.
 * - The `email` property is mandatory and must adhere to a valid format.
 * - Arrays like `userGroupIds` and `qualifications` should be handled with care, ensuring that
 *   their contents conform to expected data structures.
 *
 * @extends Person
 * @param id A unique identifier for the employee; may be null for new employees.
 * @param email Email address of the employee. Must be a valid email format.
 * @param password The employee’s password; may be null when omitted (e.g., on update requests).
 * @param shorthandName A short, human-readable alias for the employee, often used as a display name.
 * @param birthday The employee’s date of birth. Must be a valid `Date` object.
 * @param contract Details about the employee’s contract. Null if not applicable or not yet set.
 * @param qualifications A list of qualifications associated with the employee, or null if none are defined.
 * @param officeId The unique identifier for the office/location where the employee is assigned.
 * @param contactInfo A list of communication methods or contact points for the employee; null if not provided.
 * @param userGroupIds An array of numeric group IDs that determines the employee's system permissions or roles.
 */
export interface EmployeeRequest extends Person {
    id: Nullable<number>
    email: string,
    password: Nullable<string>
    shorthandName: string
    birthday: Date
    contract: Nullable<EmployeeContractRequest>
    qualifications: Nullable< EmployeeQualification[] >
    officeId: number
    contactInfo: Nullable< Communication[] >
    userGroupIds: number[]
}

export interface EmployeeContractRequest {
    employeeContractId: number
    userId: number
    employerId: number
    employmentStatus: string
    daysOfHoliday: number
    workhoursPerWeek: number
    contractualHoursOfOvertimePerWeek: number
    internalCostsPerHour: number
    daysOfWork: Workweek
    start: Nullable<Date>
    end: Nullable<Date>
    isDeleted: boolean
}

export interface EmployeeContractId {
    employeeContractId: Identifier<number>
}

export interface EmployeeContract extends EmployeeContractId, UserIdProp {
    employer: Employer
    employmentStatus: string
    daysOfHoliday: number
    workhoursPerWeek: WorkingHours
    contractualHoursOfOvertimePerWeek: WorkingHours
    internalCostsPerHour: Money
    daysOfWork: Workweek
    start: Nullable<Date>
    end: Nullable<Date>
    isDeleted: boolean
}


export interface EmployersId {
    employersId: Identifier<number>
}

export interface Employer extends EmployersId, SoftDeletable, Corporation, TaxIdentifiable {
    offices: EmployersOffice[]
    bankingInfo: Nullable< Sensitive< EmployerBankingInfo[] > >
}

export interface EmployersOffice extends InvoiceOffice, EmployersId {

}

export interface IEmploymentStatus {
    [ key: string ]: string
}

export const EmploymentStatus: IEmploymentStatus = {
    PERMANENT: "PERMANENT",
    APPRENTICE: "APPRENTICE",
    FREELANCER: "FREELANCER",
    SHORT_TERM: "SHORT_TERM",
    TRAINEE: "TRAINEE",
    MINOR_EMPLOYMENT: "MINOR_EMPLOYMENT"
};

export interface EmployeeQualificationId {
    employeeQualificationId: Identifier<number>
}

export interface EmployeeQualification extends Qualification, SoftDeletable, EmployeeQualificationId, UserIdProp {
    lastRefresh: Date
    nextRefresh: Nullable<Date>
}

